import gsap from "gsap";
import Cache from "../Cache";
import Common from "../Common";
import * as PIXI from "../core/PIXI";
import { Comm } from "./comm/Comm";
import { EVENTS } from "./EVENTS";
import { Value } from "./Value";
import {v1} from "uuid";

let message_id = 1000;


export class Controller
{

    static token: string;
    static hash: string;
    static game_id: number = 196;
    static apiUrl: string;
    static chatChannel: string;
    static player: {
        name: string;
        id: string;
        balance: number;
    };

    static betLimit: Comm.ResponseTypes.Limit;
    static cashoutLimit: Comm.ResponseTypes.Limit;
    static coinDenominations: Array<number> = [1, 2, 5, 10];

    static messages: Array<Comm.ResponseTypes.Chat> = [];

    static state: Comm.State = "init";
    static stateUpdated: boolean;
    static gameRoundId: string;
    static multiplier: number;

    static resultHistory: Array<Comm.ResponseTypes.History> = [];

    static updatedAt: string;

    static bet_history: Record<string, Comm.ResponseTypes.BetData> = {};

    static active_bets: Array<Comm.ResponseTypes.BetData> = [];
    static my_bets: Array<Comm.ResponseTypes.BetData> = [];
    static all_bets: Array<Comm.ResponseTypes.BetData> = [];
    static all_bets_count: number = 0;

    static players_online: number;

    static timestampDiff: number;
    static round_start_time: number;
    static round_crash_time: number;
    static starting_state_duration: number;

    static hiddenTime: number;

    // TODO: clearing old avatars...

    static avatars: Record<string, string> = {};

    static warning: string;

    static init(token: string, hash: string, game_id: number, apiUrl: string)
    {
        this.token = token;
        this.hash = hash;
        this.game_id = game_id;
        this.apiUrl = apiUrl;

        document.addEventListener("visibilitychange", this.onVisibilityChange.bind(this));

        Comm.on(Comm.RESPONSE.GAME_INFO, this.onGameInfo, this, true);
        
        Comm.on(Comm.RESPONSE.INITIALIZE, this.onSocketInitialize, this, true);
        Comm.on(Comm.RESPONSE.GAME_STATE, this.onSocketGameState, this, true);
        Comm.on(Comm.RESPONSE.GAME_STATE_STARTING, this.onSocketGameStarting, this, true);
        Comm.on(Comm.RESPONSE.GAME_STATE_IN_PROGRESS, this.onSocketGameProgress, this, true);
        Comm.on(Comm.RESPONSE.GAME_STATE_CRASH, this.onSocketGameCrash, this, true);
        Comm.on(Comm.RESPONSE.CASHED_IN, this.onSocketCashedIn, this, true);
        Comm.on(Comm.RESPONSE.CASHED_OUT, this.onSocketCashedOut, this, true);

        Comm.on(Comm.RESPONSE.BALANCE_UPDATE, this.onBalanceUpdate, this, true);
        Comm.once(Comm.RESPONSE.MY_BETS, this.onMyBetsResponse, this, true);
        Comm.on(Comm.RESPONSE.BET, this.onBetResponse, this, true);
        Comm.on(Comm.RESPONSE.CANCEL_BET, this.onCancelBetResponse, this, true);
        Comm.on(Comm.RESPONSE.CASHOUT, this.onCashoutResponse, this, true);

        Comm.on(Comm.RESPONSE.ERROR, this.onSocketError, this, true);

        Comm.on(Comm.RESPONSE.CHAT, this.onChatMessage, this, true);

        Comm.on(Comm.RESPONSE.CHAT_PLAYERS_COUNT, this.onPlayersCount, this, true);
        Comm.on(Comm.RESPONSE.CHAT_PLAYERS_JOIN, this.onPlayersJoin, this, true);
        Comm.on(Comm.RESPONSE.CHAT_PLAYERS_LEAVE, this.onPlayersLeave, this, true);

        Comm.on(Comm.RESPONSE.MY_BETS, this.onMyBetsResponse, this, true);

        Comm.on(Comm.RESPONSE.MY_BETS, this.addMyBetsToHistory, this, true);
        Comm.on(Comm.RESPONSE.PREVIOUS_BETS, this.addPreviousBetsToHistory, this, true);
        Comm.on(Comm.RESPONSE.LEADERBOARD, this.addLeaderboardsToHistory, this, true);

        Comm.on(Comm.CONNECTION.LOST, this.onCommConnectionLost, this);
        Comm.on(Comm.CONNECTION.RECONNECTING, this.onCommReconnecting, this);
        Comm.on(Comm.CONNECTION.DISCONNECTED, this.onCommDisconnected, this);
        Comm.on(Comm.CONNECTION.RECONNECTED, this.onCommReconnected, this);

        Comm.init(this.apiUrl, this.game_id, this.hash);
    }


    static onCommConnectionLost()
    {
        this.warning = notifications.warning(window.lang.shared.components.connector["slow connection"], this.warning);
    }
    

    static onCommReconnecting()
    {
        this.warning = notifications.warning(lang.shared.components.connector.reconnect, this.warning);
    }
    

    static onCommDisconnected()
    {
        // this.warning = notifications.warning(lang.shared.components.connector.disconnected, this.warning);
        this.warning = notifications.warning("Disconnected!", this.warning);
    }
    

    static onCommReconnected()
    {
        notifications.close(this.warning);
    }
    

    static handlePassedTweens()
    {
        // tick pixi ticker...
        PIXI.Ticker.shared.update();

        // fake ticker time
        const tickerTime = (performance.now() * .001 - this.hiddenTime);
        // kill tweens before advancing time... we handle it in kill delayed calls...
        const tweens = gsap.globalTimeline.getChildren().filter(tween =>
        {
            return tickerTime - tween.startTime() - tween.duration() >= 0;
        });
        tweens.forEach(tween => tween.kill());
        // advance time
        gsap.ticker.tick();
        // handle passed tweens...
        Common.recursiveUpdateTweens(tweens);
    }


    private static onVisibilityChange()
    {
        if (document.visibilityState == "hidden")
        {
            gsap.ticker.lagSmoothing(0);
            this.hiddenTime = performance.now() * .001 - gsap.ticker.time;
        }
        else
        {
            this.handlePassedTweens();
            gsap.ticker.lagSmoothing(500, 33);
        }
    }


    static clearCache()
    {
        Cache.setJson("avatars", {});
    }


    static onGameInfo(data: Comm.ResponseTypes.GameInfo)
    {
        global.currency = data.result.player.currency;

        this.player = {
            name: data.result.player.username,
            id: data.result.player.id,
            balance: data.result.player.balance
        };

        Value.getNumber(Value.ID.BALANCE, data.result.player.balance, Value.Options.NoEdit);

        if (Cache.get("version") != version)
        {
            this.clearCache();
            Cache.set("version", version);
        }

        this.avatars = Cache.getJson("avatars") || {};

        this.chatChannel = data.result.chat.channel;

        this.messages = data.result.chat.history;
        this.messages.forEach(message => message.id = message_id++);

        EVENTS.emit(EVENTS.RESPONSE.PLAYER_UPDATE);
    }


    private static onSocketInitialize(data: Comm.ResponseTypes.Init)
    {
        this.starting_state_duration = data.starting_state_duration * 1000;

        const server_timestamp = data.server_timestamp * 1000;
        const round_start_time = data.round_start_time * 1000;
        const client_timestamp = performance.now();

        this.timestampDiff = server_timestamp - client_timestamp;
        this.round_start_time = round_start_time - this.timestampDiff;

        this.round_crash_time = null;

        this.gameRoundId = data.game_round_id;
        this.state = data.state;
        this.stateUpdated = true;

        this.betLimit = data.game.limits.bet;
        this.cashoutLimit = data.game.limits.cashout_at;
        this.coinDenominations = data.game.limits.coin_denominations;

        Value.getNumber(Value.ID.MIN_BET, data.game.limits.bet.min, Value.Options.NoEdit);
        Value.getNumber(Value.ID.MAX_BET, data.game.limits.bet.max, Value.Options.NoEdit);

        Value.getNumber(Value.ID.MIN_CASHOUT, data.game.limits.cashout_at.min, Value.Options.NoEdit);
        Value.getNumber(Value.ID.MAX_CASHOUT, data.game.limits.cashout_at.max, Value.Options.NoEdit);

        Value.getNumber(Value.ID.MAX_WIN, data.game.limits.max_win, Value.Options.NoEdit);

        this.active_bets = Object.values(data.active_player_bets);

        this.all_bets = Object.values(data.all_bets).sort((a, b) => Number(b.bet_amount) - Number(a.bet_amount));

        this.all_bets_count = data.total_bet_count;

        this.multiplier = data.plane?.distance;

        this.resultHistory = data.game.history;

        Comm.send(Comm.REQUEST.MY_BETS, {});

        EVENTS.emit(EVENTS.RESPONSE.ALL_BETS_UPDATE, this.all_bets);
    }


    private static onSocketGameState(data: Comm.ResponseTypes.GameState)
    {
        this.stateUpdated = this.state !== data.state;
        this.state = data.state;

        const idata = data as Comm.ResponseTypes.Init;
        if (idata.server_timestamp)
        {
            const server_timestamp = idata.server_timestamp * 1000;
            const client_timestamp = performance.now();

            const timestampDiff = (server_timestamp - client_timestamp);
            
            if (timestampDiff > this.timestampDiff)
            {
                this.timestampDiff = timestampDiff;
            }
        }
    }


    private static onSocketGameStarting(data: Comm.ResponseTypes.GameStateStarting)
    {
        const round_start_time = data.round_start_time * 1000;
        this.round_start_time = round_start_time - this.timestampDiff;
        this.round_crash_time = null;

        this.my_bets.forEach(bet =>
        {
            if (bet.game_round_id.toString() == this.gameRoundId)
            {
                bet.crashpoint = this.multiplier.toFixed(2);
            }
        });


        this.all_bets = [];
        this.all_bets_count = 0;

        this.gameRoundId = data.game_round_id;


        EVENTS.emit(EVENTS.RESPONSE.ALL_BETS_UPDATE, this.all_bets);
        EVENTS.emit(EVENTS.RESPONSE.MY_BETS_UPDATE, this.my_bets);
    }


    private static onSocketGameProgress(data: Comm.ResponseTypes.GameState, init: boolean)
    {
        this.multiplier = Math.floor(data.plane.distance * 100) * .01;
        if (!init && this.stateUpdated)
        {
            this.round_start_time = performance.now();
        }
    }


    private static onSocketGameCrash(data: Comm.ResponseTypes.GameState)
    {
        this.multiplier = Math.floor(data.plane.distance * 100) * .01;
        this.round_crash_time = performance.now();

        if (!this.resultHistory.find(d => d.game_round_id == this.gameRoundId))
        {
            this.resultHistory.unshift({
                result: this.multiplier,
                game_round_id: this.gameRoundId,
                timestamp: Common.getTimestamp()
            });
        }
        

    }


    private static onSocketCashedIn(data: Comm.ResponseTypes.CashedIn)
    {
        // for (const id in data.bets)
        // {
        //     this.all_bets.push(data.bets[id]);
        // }
        this.all_bets = Object.values(data.bets).sort((a, b) => Number(b.bet_amount) - Number(a.bet_amount));

        for (const id in this.all_bets)
        {
            this.bet_history[id] = this.all_bets[id];
        }

        this.all_bets_count = data.total_bet_count;

        EVENTS.emit(EVENTS.RESPONSE.ALL_BETS_UPDATE, this.all_bets);
    }


    private static onSocketCashedOut(data: Comm.ResponseTypes.CashedOut)
    {
        for (const id in data.bets)
        {
            const bet = this.all_bets.find(bet => bet.bet_id == data.bets[id].bet_id);
            if (bet)
            {
                bet.win_amount = data.bets[id].win_amount;
                bet.multiplier = data.bets[id].multiplier;
                bet.timestamp = data.bets[id].timestamp;
            }
            else
            {
                console.warn("adding non exsisting bet: " + data.bets[id].bet_id);
                this.all_bets.push(data.bets[id]);
            }
        }
        EVENTS.emit(EVENTS.RESPONSE.ALL_BETS_UPDATE, this.all_bets);
    }


    static onBalanceUpdate(data: Comm.ResponseTypes.BalanceUpdate)
    {
        this.player.balance = data.balance;
        Value.getNumber(Value.ID.BALANCE, data.balance);
    }


    static onMyBetsResponse(data: Comm.ResponseTypes.MyBets)
    {
        this.my_bets = data.bets;

        EVENTS.emit(EVENTS.RESPONSE.MY_BETS_UPDATE, this.my_bets);
    }


    static placeBet(amount: number, cashout_at?: number)
    {
        let uuid: string;
        if (this.player.balance >= amount)
        {
            uuid = Comm.send(Comm.REQUEST.BET, {
                amount: amount,
                cashout_at: cashout_at
            }, true);
        }
        else
        {
            uuid = v1();

            setTimeout(() => {
                Comm.emit(Comm.RESPONSE.BET, {
                    error: "INSUFFICIENT_FUNDS",
                    uuid: uuid,
                    bet_id: null
                });
            }, 10);
        }

        this.active_bets.push({
            player_id: Number(Controller.player.id),
            username: this.player.name,
            player_avatar: Controller.getAvatarForUser(Controller.player.id.toString()),
            currency: currency,
            bet_amount: amount.toFixed(2),
            game_round_id: Number(this.gameRoundId),
            cashout_at: cashout_at,
            timestamp: Common.getTimestamp(),
            uuid: uuid,
            bet_id: null,
            win_amount: null,
            multiplier: null,
            crashpoint: null
        });

        return uuid;
    }


    static onBetResponse(data: Comm.ResponseTypes.Bet)
    {
        const idx = this.active_bets.findIndex(b => data.uuid == b.uuid);

        if (data.error)
        {
            this.active_bets.splice(idx, 1);

            const lang = window.lang.shared["ui-components"].toastr["toastr-message"];
            switch (data.error)
            {
                case "INVALID_GAME_STATE":
                    notifications.error(lang["Stage time out"]);
                    break;

                case "INSUFFICIENT_FUNDS":
                    notifications.error(lang["Not enough balance"]);
                    break;

                default:
                    notifications.error(lang["Something went wrong"]);
            }
        }
        else
        {
            this.active_bets[idx].bet_id = data.bet_id;
            this.active_bets[idx].timestamp = Common.getTimestamp();
        }
    }


    static cancel(id: number)
    {
        const uuid = Comm.send(Comm.REQUEST.CANCEL, {
            bet_id: id
        }, true);

        const idx = this.active_bets.findIndex(b => id == b.bet_id);
        if (idx !== -1)
        {
            this.active_bets[idx].uuid = uuid;
        }
        else
        {
            console.warn("no bet with id: " + id);
        }

        return uuid;
    }


    static onCancelBetResponse(data: Comm.ResponseTypes.CancelBet)
    {
        const idx = this.active_bets.findIndex(b => data.uuid == b.uuid);

        if (data.error)
        {
            const lang = window.lang.shared["ui-components"].toastr["toastr-message"];
            switch (data.error)
            {
                case "INVALID_GAME_STATE":
                    notifications.error(lang["Stage time out"]);
                    break;

                case "INSUFFICIENT_FUNDS":
                    notifications.error(lang["Not enough balance"]);
                    break;

                default:
                    notifications.error(lang["Something went wrong"]);
            }
        }
        else
        {
            this.active_bets.splice(idx, 1);
        }
    }


    static cashout(id: number)
    {
        const uuid = Comm.send(Comm.REQUEST.CASHOUT, {
            bet_id: id
        }, true);

        const idx = this.active_bets.findIndex(b => id == b.bet_id);
        this.active_bets[idx].uuid = uuid;

        return uuid;
    }


    static onCashoutResponse(data: Comm.ResponseTypes.Cashout)
    {
        const bet = this.active_bets.find(bet => (data.uuid && bet.uuid == data.uuid) || bet.bet_id == data.bet_id);
        if (!bet)
        {
            return;
        }

        if (data.error)
        {
            const lang = window.lang.shared["ui-components"].toastr["toastr-message"];
            switch (data.error)
            {
                case "INVALID_GAME_STATE":
                    notifications.error(lang["Stage time out"]);
                    break;

                default:
                    notifications.error(lang["Something went wrong"]);
            }
        }
        else
        {
            bet.multiplier = Number(data.win_amount) > 0 ? data.multiplier : null;
            bet.win_amount = Number(data.win_amount) > 0 ? data.win_amount : null;
            bet.timestamp = Common.getTimestamp();

            if (Number(bet.win_amount))
            {
                notifications.win(
                    Number(bet.multiplier),
                    Number(bet.win_amount),
                    bet.currency,
                    Number(bet.win_amount) > Number(bet.bet_amount)
                );
            }

            this.active_bets.splice(this.active_bets.indexOf(bet), 1);

            this.bet_history[bet.bet_id] = bet;
            this.my_bets.unshift(bet);

            EVENTS.emit(EVENTS.RESPONSE.MY_BETS_UPDATE, this.my_bets);

            if (Number(bet.win_amount))
            {
                EVENTS.emit(EVENTS.GAME.PLAYER_CASHOUT, bet);
            }
        }

    }


    private static onSocketError(data: Comm.ResponseTypes.Error)
    {
        EVENTS.emit(EVENTS.GAME.CRITICAL_ERROR);
    }


    private static onChatMessage(data: Comm.ResponseTypes.Chat)
    {
        data.id = message_id++;
        this.messages.push(data);
    }


    private static onPlayersCount(data: Comm.ResponseTypes.ChatPlayersCount)
    {
        this.players_online = data.length;
        EVENTS.emit(EVENTS.GAME.PLAYERS_COUNT, this.players_online);
    }


    private static onPlayersJoin(data: Comm.ResponseTypes.ChatPlayersCount)
    {
        this.players_online++;
        EVENTS.emit(EVENTS.GAME.PLAYERS_COUNT, this.players_online);
    }


    private static onPlayersLeave(data: Comm.ResponseTypes.ChatPlayersCount)
    {
        this.players_online--;
        EVENTS.emit(EVENTS.GAME.PLAYERS_COUNT, this.players_online);
    }


    static getFlyingTime()
    {
        let time = this.round_crash_time || performance.now();
        return time - this.round_start_time;
    }


    static getCountdownTime()
    {
        return this.round_start_time - performance.now();
    }


    static getStartingTime()
    {
        return performance.now() - this.round_start_time + this.starting_state_duration;
    }


    static getCountdownPercentage()
    {
        return Math.max(Math.min((this.round_start_time - performance.now()) / this.starting_state_duration, 1), 0);
    }


    private static betCopy: Comm.ResponseTypes.BetData;


    static setBetCopy(data: Comm.ResponseTypes.BetData)
    {
        this.betCopy = data;
    }


    static getBetCopy(id: string)
    {
        if (this.bet_history[id])
        {
            return this.bet_history[id];
        }
        else if (this.betCopy.bet_id.toString() == id)
        {
            return this.betCopy;
        }
        else
        {
            return false;
        }
    }


    static addMyBetsToHistory(data: Comm.ResponseTypes.MyBets)
    {
        data.bets.forEach(bet =>
        {
            this.bet_history[bet.bet_id] = bet;
        });
    }


    static addPreviousBetsToHistory(data: Comm.ResponseTypes.PreviousBets)
    {
        data.previous_round_bets.forEach(bet =>
        {
            this.bet_history[bet.bet_id] = bet;
        });
    }


    static addLeaderboardsToHistory(data: Comm.ResponseTypes.Leaderboard)
    {
        data.data.forEach(bet =>
        {
            this.bet_history[bet.bet_id] = bet;
        });
    }


    static getAvatarForUser(id: string): string
    {
        id = id.toString();

        if (id == "bot")
        {
            return "bot";
        }

        if (this.avatars[id])
        {
            return this.avatars[id];
        }

        let avatar = 0;

        for (let i = 0; i < id.length; i++)
        {
            avatar += Number("0x" + id[i]);
        }

        this.avatars[id] = ((avatar % 72) + 1).toString();

        return this.avatars[id];
    }


    static setAvatarForUser(id: string, avatar: string)
    {
        if (avatar != this.avatars[id])
        {
            this.avatars[id] = avatar;

            Cache.setJson("avatars", this.avatars);

            if (id == Controller.player.id)
            {
                EVENTS.emit(EVENTS.RESPONSE.PLAYER_UPDATE);
            }

            this.all_bets.forEach(bet =>
            {
                if (bet.player_id.toString() == id)
                {
                    bet.player_avatar = avatar;
                }
            });

            EVENTS.emit(EVENTS.RESPONSE.ALL_BETS_UPDATE, this.all_bets);
        }

    }

}


// @ts-ignore
window.Controller = Controller;